import dayjs from 'dayjs';
import {ALPHABET, CROSS_GRADE} from 'utils/consts';
import errorLogger from 'utils/errors';

/**
 * Converts a timestamp to a date string in the format of MM/DD/YYYY.
 * @param {string} ts - The timestamp to convert.
 * @returns {string} The formatted date string.
 */
export function tsToNy(ts) {
  try {
    const utcDate = new Date(ts);
    const usaTime = utcDate.toLocaleString('en-US', {
      timeZone: 'America/New_York',
    });
    return usaTime;
  } catch (e) {
    errorLogger.logError(e, {
      context: {
        operation: 'tsToNy',
        timestamp: ts,
        errorType: 'utility',
      },
    });
    return '';
  }
}

/**
 * Returns the percentage of each number in the array.
 * @param {number[]} nums - The array of numbers.
 * @returns {string[]} The array of percentages.
 */
export const getPercentages = (nums) => {
  // if sum is 0, return array of 0s
  const sum = nums.reduce((a, b) => a + b, 0);
  if (sum === 0) {
    return nums.map(() => '0');
  }
  return nums.map((num) => ((num / sum) * 100).toFixed(2));
};

/**
 * Calculates the gradespan. If starting or ending don't exist, returns [].
 * @param {number} starting - The starting grade.
 * @param {number} ending - The ending grade.
 * @param {boolean} crossGradeEnabled - Whether to include cross grade.
 * @returns {Array<string|number>} The gradespan array.
 */
export const calculateGradespan = (starting, ending, crossGradeEnabled) => {
  if (starting === null || ending === null) return [];
  if (starting > ending) return [];
  const gradespan = [];
  for (let i = starting; i <= ending; i++) {
    gradespan.push(i);
  }
  if (crossGradeEnabled) {
    gradespan.push(CROSS_GRADE);
  }
  return gradespan;
};

/**
 * Returns number if it is between min and max, otherwise returns min or max.
 * @param {number} num - The number to clamp.
 * @param {number} min - The minimum value.
 * @param {number} max - The maximum value.
 * @returns {number} The clamped number.
 */
export function clamp(num, min, max) {
  if (Number.isNaN(Number(num))) return min;
  return num <= min ? min : num >= max ? max : num;
}

/**
 * Compares two objects by the length of their data arrays and their last y values.
 * @param {Object} a - The first object.
 * @param {Object} b - The second object.
 * @returns {number} 1 if a is greater than b, -1 if b is greater than a, 0 if equal.
 */
export const compareByLength = (a, b) => {
  // handle case where data arrays are undefined or null
  if (!a.data && !b.data) return 0;
  if (!a.data) return -1;
  if (!b.data) return 1;

  // handle case where data arrays are empty
  if (a.data.length === 0 && b.data.length === 0) return 0;
  if (a.data.length === 0) return -1;
  if (b.data.length === 0) return 1;

  // Compare values if both arrays have data and valid y values
  if (
    Array.isArray(a.data) &&
    Array.isArray(b.data) &&
    a.data.length > 0 &&
    b.data.length > 0 &&
    a.data[a.data.length - 1].y !== undefined &&
    b.data[b.data.length - 1].y !== undefined
  ) {
    const aValue = a.data[a.data.length - 1].y;
    const bValue = b.data[b.data.length - 1].y;
    if (aValue > bValue) return 1;
    if (aValue < bValue) return -1;
  }

  return 0;
};

/**
 * Parses a date and returns a string with the operator and date.
 * @param {string} date - The date to parse.
 * @param {string} [operator] - The operator to use.
 * @param {string} [format='YYYY-MM-DD'] - The date format.
 * @returns {string} The parsed date string.
 */
export const parseDate = (date, operator, format = 'YYYY-MM-DD') => {
  let value = date;
  if (
    !date ||
    date === 'null' ||
    date === 'undefined' ||
    date === 'NaN' ||
    date === 'Invalid date'
  ) {
    return operator ?? '';
  }
  if (operator === 'BETWEEN') {
    const dates = value.toString().split(',');
    try {
      const parsedDates = dates
        .map((element) => {
          if (
            !element ||
            element === 'null' ||
            element === 'undefined' ||
            element === 'NaN' ||
            element === 'Invalid date'
          ) {
            return '';
          } else {
            return dayjs(element, format).format(format);
          }
        })
        .join(' - ');
      const returnVal = `BETWEEN ${parsedDates}`;
      return returnVal;
    } catch (e) {
      errorLogger.logError(e, {
        context: {
          operation: 'parseDate',
          operator: 'BETWEEN',
          value: dates,
          format,
          errorType: 'utility',
        },
      });
      return 'BETWEEN';
    }
  } else {
    const oper = operator ?? '';
    try {
      value = dayjs(value, format).format(format);
    } catch (e) {
      errorLogger.logError(e, {
        context: {
          operation: 'parseDate',
          operator: oper,
          value,
          format,
          errorType: 'utility',
        },
      });
      return oper;
    }
    return `${oper} ${value}`;
  }
};

/**
 * Parses a number and returns a string with the operator and number.
 * @param {number|string} number - The number to parse.
 * @param {string} [operator] - The operator to use.
 * @returns {string} The parsed number string.
 */
export const parseNumber = (number, operator) => {
  if (
    (!number && number !== 0) ||
    number === 'null' ||
    number === 'undefined' ||
    number === 'NaN'
  ) {
    return operator ?? '';
  }
  let value = number;
  if (operator === 'BETWEEN') {
    const numbers = value.toString().split(',');
    try {
      const parsedNumbers = numbers
        .map((element) => {
          if (
            (!element && number !== 0) ||
            element === 'null' ||
            element === 'undefined' ||
            element === 'NaN'
          ) {
            return '';
          } else {
            return parseFloat(element);
          }
        })
        .join(' - ');
      const returnVal = `BETWEEN ${parsedNumbers}`;
      return returnVal;
    } catch (e) {
      errorLogger.logError(e, {
        context: {
          operation: 'parseNumber',
          operator: 'BETWEEN',
          value: numbers,
          errorType: 'utility',
        },
      });
      return 'BETWEEN';
    }
  } else {
    const oper = operator ?? '';
    try {
      value = parseFloat(number);
    } catch (e) {
      errorLogger.logError(e, {
        context: {
          operation: 'parseNumber',
          operator: oper,
          value: number,
          errorType: 'utility',
        },
      });
      return oper;
    }
    if (isNaN(value)) {
      return oper;
    }
    return `${oper} ${value}`;
  }
};

/**
 * Generates a unique id that is not in the existing array.
 * @param {Array<string|number>} [existing=[]] - The array of existing ids.
 * @param {string} [idType='string'] - The type of id to generate ('string' or 'number').
 * @returns {string|number} The generated unique id.
 */
export const generateUniqueId = (existing = [], idType = 'string') => {
  if (!Array.isArray(existing)) {
    existing = [];
  }
  // Generate a random 5 digit number.
  const id =
    idType === 'string'
      ? Math.random().toString(36).substring(2, 9)
      : Math.floor(Math.random() * 90000) + 10000;
  while (existing.includes(id)) {
    generateUniqueId(existing);
  }
  return id;
};

/**
 * Returns the letter at the specified index in the alphabet.
 * @param {number} index - The index of the letter.
 * @returns {string} The letter at the specified index.
 */
export const getLetterByIndex = (index) => {
  if ((!index && index !== 0) || index < 0) {
    return '';
  } else if (index >= ALPHABET.length) {
    return ALPHABET[index % ALPHABET.length];
  }
  return ALPHABET[index];
};

/**
 * Strips HTML tags from a string, allowing only specified tags.
 * @param {string} input - The input string.
 * @param {string} [allowed='<a><b><strong><i><em><p><br><ul><ol><li><span><div>'] - The allowed tags.
 * @returns {string} The string with HTML tags stripped.
 */
export const stripTags = (
  input,
  allowed = '<a><b><strong><i><em><p><br><ul><ol><li><span><div>'
) => {
  if (!input) return '';
  const tags = /<\/?([a-z][a-z0-9]*)\b[^>]*>/gi;
  const safeAllowed =
    allowed || '<a><b><strong><i><em><p><br><ul><ol><li><span><div>';
  const output = input.toString().replace(tags, ($0, $1) => {
    return safeAllowed.indexOf('<' + $1.toLowerCase() + '>') > -1 ? $0 : '';
  });
  return output;
};

/**
 * Extracts text content from an HTML string.
 * @param {string} htmlString - The HTML string.
 * @returns {string[]} The array of text content.
 */
export const extractHTMLTextContent = (htmlString) => {
  const fragment = document.createDocumentFragment();
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;
  while (tempDiv.firstChild) {
    fragment.appendChild(tempDiv.firstChild);
  }
  const strArray = Array.from(fragment.childNodes).map(
    (node) => node.textContent
  );
  return strArray;
};

/**
 * Converts a string to a float, and returns the number if it is a valid number.
 * Otherwise, returns an empty string.
 * @param {string} str - The string to convert.
 * @returns {number|string} The converted float or an empty string.
 */
export const stringToFloat = (str) => {
  if (str === '') {
    return '';
  }
  const num = parseFloat(str);
  return isNaN(num) ? '' : num;
};

/**
 * Decides on the color of the text based on the background color.
 * @param {string} hexcolor - The background color in hex format.
 * @returns {string} The text color.
 */
export const setContrast = (hexcolor) => {
  if (!hexcolor || !hexcolor.match(/^#([0-9A-F]{3}|[0-9A-F]{6,8})$/i)) {
    return '#000000d9';
  }
  let color = hexcolor;
  // if the hex color is 3 digits, convert it to 6 digits
  if (hexcolor.length === 4) {
    color = `#${hexcolor[1]}${hexcolor[1]}${hexcolor[2]}${hexcolor[2]}${hexcolor[3]}${hexcolor[3]}`;
  }
  // convert hex to rgb
  const r = parseInt(color.substring(1, 3), 16);
  const g = parseInt(color.substring(3, 5), 16);
  const b = parseInt(color.substring(5, 7), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // If YIQ is greater than 128, return black, else return white
  return yiq >= 128 ? '#000000d9' : 'white';
};

/**
 * Returns the weighted width of a string based on the character types.
 * @param {string} str - The string to calculate the width of.
 * @returns {number} The weighted width of the string.
 */
const charWidthLookup = {
  narrow: 4,
  average: 8,
  wide: 12,
};

const narrowChars = new Set('iIl|.,:;!()-');
const wideChars = new Set('wm_');

export const getWeightedWidth = (str, base = 250) => {
  return (
    str?.split('').reduce((acc, char) => {
      if (narrowChars.has(char)) return acc + charWidthLookup.narrow;
      if (char === char.toUpperCase() || wideChars.has(char))
        return acc + charWidthLookup.wide;
      return acc + charWidthLookup.average;
    }, 0) || base
  );
};

/**
 * Returns true or false based on the value of the input.
 * @param {string|number|boolean|null|undefined} value - The value to check.
 * @param {string} [outputType='boolean'] - The type of output to return ('string' or 'boolean').
 * @returns {string|boolean} 'true' or 'false' as a string, or boolean based on outputType.
 */
export const isTrue = (value, outputType = 'boolean') => {
  let output;
  if (
    value === null ||
    value === undefined ||
    value === 'null' ||
    value === 'undefined' ||
    value === ''
  ) {
    output = false;
  } else if (typeof value === 'boolean') {
    output = value;
  } else if (typeof value === 'number') {
    output = value === 1;
  } else if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    output =
      trimmed === 'true' ||
      trimmed === '1' ||
      trimmed === 'yes' ||
      trimmed === 'y';
  } else if (
    Array.isArray(value) ||
    typeof value === 'object' ||
    typeof value === 'function'
  ) {
    output = false;
  } else {
    output = Boolean(value);
  }
  if (outputType === 'boolean') {
    return Boolean(output);
  } else if (outputType === 'string') {
    return String(output);
  }
  return output;
};

/**
 * Converts a string representation of a JSON object to an actual JSON object.
 * @param {string} str - The string to convert.
 * @returns {Object} The converted JSON object.
 */
export const stringToJson = (str) => {
  // Parse the source if it's a string representation of an array
  let jsonObj = str;
  if (typeof str === 'string' && str.startsWith('[') && str.endsWith(']')) {
    try {
      // Remove single quotes and replace with double quotes for valid JSON
      const jsonString = str
        .replace(/'/g, '"') // Replace single quotes with double quotes
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim(); // Remove extra whitespace

      jsonObj = JSON.parse(jsonString);
    } catch (error) {
      errorLogger.logError(error, {
        context: {
          operation: 'stringToJson',
          inputString: str.substring(0, 100), // Limit to first 100 chars
          errorType: 'utility',
        },
      });
      // Fallback: try simple string parsing if JSON.parse fails
      jsonObj = str
        .replace(/^\[|\]$/g, '') // Remove brackets
        .split(',') // Split by comma
        .map(
          (item) =>
            item
              .trim() // Trim whitespace
              .replace(/^'|'$/g, '') // Remove quotes
              .replace(/^"|"$/g, '') // Remove double quotes too
        );
    }
  }
  return jsonObj;
};

/**
 * Standardizes datatypes for use in the application.
 * @param {string} datatype
 * @returns {string} ['STRING', 'INTEGER', 'DATE', 'BOOLEAN']
 */
export const standardizeDataType = (datatype) => {
  if (!datatype) {
    return 'STRING';
  }
  const int_types = [
    'bigint',
    'bigserial',
    'dec',
    'double',
    'float',
    'int',
    'integer',
    'mediumint',
    'numeric',
    'real',
    'serial',
    'smallint',
    'tinyint',
  ];
  const str_types = [
    'binary',
    'blob',
    'char',
    'enum',
    'json',
    'longblob',
    'longtext',
    'mediumblob',
    'mediumtext',
    'text',
    'tinyblob',
    'tinytext',
    'uuid',
    'varbinary',
    'varchar',
  ];
  const date_types = ['date', 'time', 'year', 'timestamp', 'datetime'];
  let data_type = datatype.toLowerCase().split('(')[0].trim();

  if (int_types.includes(data_type)) {
    return 'INTEGER';
  } else if (str_types.includes(data_type)) {
    return 'STRING';
  } else if (date_types.includes(data_type)) {
    return 'DATE';
  } else if ('boolean' === data_type) {
    return 'BOOLEAN';
  } else {
    // If the data type is not recognized, default to STRING
    return 'STRING';
  }
};

/**
 * Checks if a string is a valid ISO date (YYYY-MM-DD).
 * @param {string} val - The string to check.
 * @returns {boolean} True if valid ISO date, false otherwise.
 */
export const isValidISODate = (val) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) return false;
  const d = new Date(val);
  return (
    d instanceof Date && !isNaN(d) && d.toISOString().split('T')[0] === val
  );
};

/**
 * Sorts homeroom IDs, ensuring 'unassigned' always comes first
 */
export const sortHomerooms = (a, b) => {
  if (a === b) return 0;
  if (a === 'unassigned') return -1;
  if (b === 'unassigned') return 1;
  return parseInt(a, 10) - parseInt(b, 10);
};

/**
 * Downloads a JS object as a local JSON file via the browser.
 * @param {Object} data - The data to serialize and download.
 * @param {string} filename - The filename for the downloaded file.
 */
export const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
